import { useState } from 'react';
import ClassNames from '@streamloots/classnames';
import { OrderedItemType, OrderedTipItem, PageFeed, PageFeedComment, User } from 'model/indexTS';
import { useTranslation } from 'utils/translation';
import { PageState } from 'services/page';
import { Checkbox, InputNumber, InputText } from 'ui-library/form-elements';
import { rules } from 'ui-library/forms';
import { TransparentCheckout } from 'scenes/checkout';
import trackEventManager from 'utils/event-manager';
import { Currency, PaymentMethods } from '@streamloots/streamloots-types';
import { useReactAlert } from 'components/react-alert-provider';
import theme from './checkout-tips.scss';
import { FeedCommentView } from '../feed-comments-list';

const classNames = ClassNames(theme);

const minTips = rules.minNumber({ min: 2 });

interface CheckoutTipsProps {
  feed: PageFeed;
  page: PageState;
  user: User | null;
  onClose: () => void;
  showCheckout: boolean;
}

export const CheckoutTips = ({ feed, page, user, onClose, showCheckout }: CheckoutTipsProps): JSX.Element | null => {
  const { t } = useTranslation('pageFeed');
  const [tipAmount, setTipAmount] = useState(2);
  const [messageTip, setMessageTip] = useState('');
  const [createMessageAsComment, setCreateMessageAsComment] = useState(false);
  const { showSuccess } = useReactAlert();

  const handleFeedClicked = (
    button: 'see comment preview' | 'init tip payment - stripe' | 'init tip payment - paypal',
  ) => {
    trackEventManager.feedClicked({
      location: 'tips checkout',
      button,
      content_id: feed._id,
      like_count: feed.summary.reactionCount,
    });
  };

  const handleTrackPaymentMethodChange = (value: PaymentMethods) => {
    if (value === PaymentMethods.PAYPAL) {
      handleFeedClicked('init tip payment - paypal');
    }

    if (value === PaymentMethods.STRIPE) {
      handleFeedClicked('init tip payment - stripe');
    }
  };

  const orderItem: OrderedTipItem[] = [
    {
      quantity: 1,
      type: OrderedItemType.Tips,
      amount: tipAmount,
      message: messageTip.trim().length === 0 ? undefined : messageTip.trim(),
      createMessageAsComment,
      feedId: feed._id,
    },
  ];

  const commentPreview: PageFeedComment = {
    createdAt: new Date(Date.now()),
    updatedAt: new Date(Date.now()),
    _id: 'preview',
    feedId: feed._id,
    pageId: page._id || 'pageId',
    userId: user?._id || 'userId',
    content: messageTip,
    username: user?.profile.username || 'username',
    avatarImageUrl: user?.profile.avatarImageUrl || 'avatar',
    deleted: false,
    publicMessage: true,
    tippedCurrency: page.currency as Currency,
    hasTipped: true,
    tippedAmount: tipAmount,
    summary: {
      liked: false,
      reactionCount: 0,
    },
  };

  const onSuccess = () => {
    onClose();
    showSuccess(t('pageFeed:tips.checkoutSuccess'));
  };

  if (page == null) {
    return null;
  }

  return (
    <TransparentCheckout
      orderedItems={orderItem}
      currency={page.currency}
      open={showCheckout}
      pageId={page._id}
      onClose={onClose}
      title={t('pageFeed:tips.descriptionCheckout', {
        slug: page.slug,
      })}
      isOwner={page.isOwner}
      onTrackPaymentMethodChange={handleTrackPaymentMethodChange}
      onSuccess={onSuccess}
    >
      <div className={classNames('checkout-tips')}>
        <h3 className={classNames('checkout-tips__title')}>{t('pageFeed:tips.title', { slug: page.slug })}</h3>
        <div>
          <div className={classNames('checkout-tips__form')}>
            <div className={classNames('checkout-tips__tip-input')}>
              <InputNumber
                decimals={2}
                defaultValue={tipAmount}
                value={tipAmount}
                showButtons={false}
                label={t('pageFeed:tips.tipAmountLabel')}
                ariaLabel={t('pageFeed:tips.tipAmountLabel')}
                wrapperClassName={classNames('checkout-tips__tip-input__wrapper')}
                onChange={value => {
                  if (typeof value === 'string') {
                    setTipAmount(Number(value));
                  } else {
                    setTipAmount(Number(value));
                  }
                }}
                required
                error={minTips(tipAmount)}
              />
            </div>
            <hr className={classNames('checkout-tips__separator')} />
            <div className={classNames('checkout-tips__tip-message')}>
              <InputText
                label={t('pageFeed:tips.messageTip')}
                placeholder={t('pageFeed:tips.messageTipPlaceholder')}
                defaultValue={messageTip}
                required={createMessageAsComment}
                onChange={value => {
                  setMessageTip(value.currentTarget.value);
                }}
              />
            </div>
          </div>
          <Checkbox
            checked={createMessageAsComment}
            onChange={() => {
              setCreateMessageAsComment(state => !state);
            }}
            label={t('pageFeed:tips.sendAsComment')}
            ariaLabel={t('pageFeed:tips.sendAsComment')}
          />
          {createMessageAsComment && (
            <div className={classNames('checkout-tips__preview')}>
              <FeedCommentView t={t} isPreview feedComment={commentPreview} />
            </div>
          )}
        </div>
      </div>
    </TransparentCheckout>
  );
};
